'use client'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Star } from 'lucide-react'
import { useState } from 'react'
import { useTranslations } from 'next-intl'
import testimonialsData from '@/content/testimonials.json'

type Testimonial = {
    name: string
    image: string
    stars: number
    text: {
        en: string
        fr: string
        nl: string
        es: string
        it: string
    }
    season: 'summer' | 'winter' | 'both'
    featured: 'home' | 'summer' | 'winter' | ''
}

type TestimonialsHomeProps = {
    locale: string
    season?: 'summer' | 'winter' | 'both'
}

export default function TestimonialsHome({ locale = 'en', season }: TestimonialsHomeProps) {
    const [showAll, setShowAll] = useState(false)
    const t = useTranslations('Testimonials')

    const allTestimonials = testimonialsData.testimonials as Testimonial[]

    let filteredTestimonials: Testimonial[]

    if (season) {
        filteredTestimonials = allTestimonials.filter((testimonial) => {
            if (season === 'both') {
                return true
            }
            return testimonial.season === season || testimonial.season === 'both'
        })
    } else {
        filteredTestimonials = allTestimonials.filter(
            (testimonial) => testimonial.featured === 'home' || (testimonial.featured === '' && testimonial.season === 'both')
        )
    }

    const featuredTestimonials = season
        ? filteredTestimonials.filter((t) => t.featured === season || t.featured === 'home')
        : filteredTestimonials.filter((t) => t.featured === 'home')

    const displayTestimonials = showAll ? filteredTestimonials : featuredTestimonials

    const title = testimonialsData.metadata.title[locale as keyof typeof testimonialsData.metadata.title] || testimonialsData.metadata.title.en

    // AggregateRating + Review structured data on the business (helps AI & rich results)
    const ratingValue = Number(
        (allTestimonials.reduce((sum, t) => sum + t.stars, 0) / allTestimonials.length).toFixed(1)
    )
    const reviewJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': 'https://www.lagrange-prapoutel.com/#organization',
        name: 'LA GRANGE',
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue,
            reviewCount: allTestimonials.length,
            bestRating: 5,
            worstRating: 1,
        },
        review: allTestimonials.slice(0, 10).map((r) => ({
            '@type': 'Review',
            author: { '@type': 'Person', name: r.name },
            reviewRating: { '@type': 'Rating', ratingValue: r.stars, bestRating: 5, worstRating: 1 },
            reviewBody: r.text[locale as keyof typeof r.text] || r.text.en,
        })),
    }

    return (
        <>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewJsonLd) }} />
        <section className="py-16 md:py-24">
            <div className="mx-auto max-w-6xl px-4 sm:px-6">
                {/* Header */}
                <div className="text-center mb-12 md:mb-16">
                    <h2 className="text-4xl md:text-5xl font-semibold font-[family-name:var(--font-bebas-neue)]">
                        {title}
                    </h2>
                </div>

                {/* Testimonials Grid */}
                <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {displayTestimonials.map((testimonial, index) => {
                        const text = testimonial.text[locale as keyof typeof testimonial.text] || testimonial.text.en
                        const initials = testimonial.name
                            .split(' ')
                            .map(n => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)

                        return (
                            <div
                                key={index}
                                className="group relative bg-card rounded-2xl p-6 border border-border/50 hover:border-border transition-colors"
                            >
                                {/* Author - Avatar + Name at top */}
                                <div className="flex items-center gap-3 mb-4">
                                    <Avatar className="size-11 ring-2 ring-background shadow-sm">
                                        <AvatarImage
                                            alt={testimonial.name}
                                            src={testimonial.image}
                                            loading="lazy"
                                            width="120"
                                            height="120"
                                        />
                                        <AvatarFallback className="text-sm font-medium bg-primary/10 text-primary">
                                            {initials}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-foreground truncate">
                                            {testimonial.name}
                                        </h3>
                                        {/* Stars inline with name */}
                                        <div className="flex items-center gap-0.5 mt-0.5">
                                            {Array.from({ length: testimonial.stars }).map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className="size-3.5 fill-yellow-400 text-yellow-400"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Quote */}
                                <blockquote>
                                    <p className="text-foreground/90 leading-relaxed font-bold">
                                        &ldquo;{text}&rdquo;
                                    </p>
                                </blockquote>
                            </div>
                        )
                    })}
                </div>

                {/* Show More Button */}
                {!showAll && filteredTestimonials.length > featuredTestimonials.length && (
                    <div className="mt-10 text-center">
                        <Button
                            onClick={() => setShowAll(true)}
                            variant="outline"
                            size="lg"
                            className="rounded-full px-8"
                        >
                            {t('showMore')}
                        </Button>
                    </div>
                )}
            </div>
        </section>
        </>
    )
}
